'use server'

import { apiRequest, ApiResponse } from '../request'

export type PrescriptionStatus =
    | 'PRESC_DRAFT'
    | 'PRESC_ACTIVE'
    | 'PRESC_SUSPENDED'
    | 'PRESC_COMPLETED'
    | 'PRESC_CANCELLED'

export type CheckStatus =
    | 'CHECK_PENDING'
    | 'CHECK_ADMINISTERED'
    | 'CHECK_SKIPPED'
    | 'CHECK_DELAYED'
    | 'CHECK_REFUSED'

export type AdministrationRoute =
    | 'ORAL'
    | 'IV'
    | 'IM'
    | 'SC'
    | 'TOPICAL'
    | 'RECTAL'
    | 'INHALATION'
    | 'SUBLINGUAL'
    | 'NASAL'
    | 'OPHTHALMIC'
    | 'OTIC'
    | 'ENTERAL'
    | 'EPIDURAL'
    | 'INTRATHECAL'

export interface PrescriptionItem {
    id: number
    prescriptionId: number
    medicationId?: number
    medication?: {
        id: number
        name: string
        activeIngredient?: string
    }
    medicationBatchId?: number
    medicationBatch?: {
        id: number
        batchNumber: string
    }
    medicationName: string
    dose: string
    unit: string
    route: AdministrationRoute
    frequency: string
    scheduleType: string
    scheduleTimes?: string[]
    intervalHours?: number
    duration?: number
    durationUnit?: string
    startDate: string
    endDate?: string
    instructions?: string
    ifNeeded: boolean
    ifNeededCondition?: string
    maxDailyDoses?: number
    dilution?: string
    infusionRate?: string
    isAntibiotic: boolean
    isContinuous: boolean
    requiresDoubleCheck: boolean
    active: boolean
}

export interface HospitalPrescription {
    id: number
    companyId: number
    hospitalId: number
    admissionId: number
    admission?: {
        id: number
        client: {
            id: number
            name: string
        }
        bed?: {
            id: number
            number: string
        }
        unit?: {
            id: number
            name: string
        }
    }
    clientId: number
    prescriptionNumber: string
    status: PrescriptionStatus
    prescriptionDate: string
    validFrom: string
    validUntil: string
    physicianId?: number
    physician?: {
        id: number
        name: string
        registrationNumber?: string
    }
    signedAt?: string
    signatureHash?: string
    notes?: string
    items?: PrescriptionItem[]
    createdAt: string
    updatedAt: string
}

export interface MedicationSchedule {
    id: number
    companyId: number
    prescriptionId: number
    itemId: number
    item?: PrescriptionItem
    prescription?: {
        id: number
        client: {
            id: number
            name: string
        }
        admission: {
            id: number
            bed?: {
                id: number
                number: string
            }
            unit?: {
                id: number
                name: string
            }
        }
    }
    scheduledTime: string
    status: CheckStatus
    administeredAt?: string
    administeredById?: number
    administeredBy?: {
        id: number
        name: string
    }
    verifiedById?: number
    verifiedBy?: {
        id: number
        name: string
    }
    skipReason?: string
    notes?: string
}

export interface CreatePrescriptionItemData {
    medicationId?: number
    medicationBatchId?: number
    medicationName: string
    dose: string
    unit: string
    route: AdministrationRoute
    frequency: string
    scheduleType: string
    scheduleTimes?: string[]
    intervalHours?: number
    duration?: number
    durationUnit?: string
    startDate?: string
    endDate?: string
    instructions?: string
    ifNeeded?: boolean
    ifNeededCondition?: string
    maxDailyDoses?: number
    dilution?: string
    infusionRate?: string
    isAntibiotic?: boolean
    isContinuous?: boolean
    requiresDoubleCheck?: boolean
}

export interface CreatePrescriptionData {
    admissionId: number
    validFrom?: string
    validUntil?: string
    notes?: string
    items: CreatePrescriptionItemData[]
}

export interface UpdatePrescriptionData {
    validUntil?: string
    notes?: string
}

export interface CheckMedicationData {
    status: CheckStatus
    notes?: string
    skipReason?: string
}

export interface PrescriptionFilters {
    hospitalId?: number
    admissionId?: number
    status?: PrescriptionStatus
    startDate?: string
    endDate?: string
}

export interface ScheduleFilters {
    admissionId?: number
    status?: CheckStatus
    startTime?: string
    endTime?: string
}

export async function getPrescriptions(
    companyId?: number,
    filters?: PrescriptionFilters
): Promise<ApiResponse<HospitalPrescription[]>> {
    return apiRequest<HospitalPrescription[]>('hospital/prescriptions', {
        params: { companyId, ...filters },
    })
}

export async function getPrescription(
    id: number
): Promise<ApiResponse<HospitalPrescription>> {
    return apiRequest<HospitalPrescription>(`hospital/prescriptions/${id}`, {})
}

export async function getActivePrescriptions(
    admissionId: number
): Promise<ApiResponse<HospitalPrescription[]>> {
    return apiRequest<HospitalPrescription[]>(
        `hospital/prescriptions/admission/${admissionId}`,
        {}
    )
}

export async function createPrescription(
    data: CreatePrescriptionData
): Promise<ApiResponse<HospitalPrescription>> {
    return apiRequest<HospitalPrescription>(
        'hospital/prescriptions',
        { data },
        'post'
    )
}

export async function updatePrescription(
    id: number,
    data: UpdatePrescriptionData
): Promise<ApiResponse<HospitalPrescription>> {
    return apiRequest<HospitalPrescription>(
        `hospital/prescriptions/${id}`,
        { data },
        'patch'
    )
}

export async function signPrescription(
    id: number
): Promise<ApiResponse<HospitalPrescription>> {
    return apiRequest<HospitalPrescription>(
        `hospital/prescriptions/${id}/sign`,
        {},
        'patch'
    )
}

export async function suspendPrescription(
    id: number,
    reason?: string
): Promise<ApiResponse<HospitalPrescription>> {
    return apiRequest<HospitalPrescription>(
        `hospital/prescriptions/${id}/suspend`,
        { params: { reason } },
        'patch'
    )
}

export async function deletePrescription(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`hospital/prescriptions/${id}`, {}, 'delete')
}

// Schedule endpoints
export async function getSchedules(
    companyId: number,
    filters?: ScheduleFilters
): Promise<ApiResponse<MedicationSchedule[]>> {
    return apiRequest<MedicationSchedule[]>(
        `hospital/prescriptions/schedules/pending/${companyId}`,
        { params: filters }
    )
}

export async function getPendingSchedules(
    companyId: number
): Promise<ApiResponse<MedicationSchedule[]>> {
    return apiRequest<MedicationSchedule[]>(
        `hospital/prescriptions/schedules/pending/${companyId}`,
        {}
    )
}

export async function checkMedication(
    scheduleId: number,
    data: CheckMedicationData
): Promise<ApiResponse<MedicationSchedule>> {
    return apiRequest<MedicationSchedule>(
        `hospital/prescriptions/schedules/${scheduleId}/administer`,
        { data },
        'post'
    )
}

export const PRESCRIPTION_STATUS_LABELS: Record<PrescriptionStatus, string> = {
    PRESC_DRAFT: 'Rascunho',
    PRESC_ACTIVE: 'Ativa',
    PRESC_SUSPENDED: 'Suspensa',
    PRESC_COMPLETED: 'Concluída',
    PRESC_CANCELLED: 'Cancelada',
}

export const PRESCRIPTION_STATUS_COLORS: Record<PrescriptionStatus, string> = {
    PRESC_DRAFT: 'badge-warning',
    PRESC_ACTIVE: 'badge-success',
    PRESC_SUSPENDED: 'badge-error',
    PRESC_COMPLETED: 'badge-ghost',
    PRESC_CANCELLED: 'badge-neutral',
}

export const CHECK_STATUS_LABELS: Record<CheckStatus, string> = {
    CHECK_PENDING: 'Pendente',
    CHECK_ADMINISTERED: 'Administrado',
    CHECK_SKIPPED: 'Pulado',
    CHECK_DELAYED: 'Atrasado',
    CHECK_REFUSED: 'Recusado',
}

export const CHECK_STATUS_COLORS: Record<CheckStatus, string> = {
    CHECK_PENDING: 'badge-warning',
    CHECK_ADMINISTERED: 'badge-success',
    CHECK_SKIPPED: 'badge-ghost',
    CHECK_DELAYED: 'badge-error',
    CHECK_REFUSED: 'badge-neutral',
}

export const ADMINISTRATION_ROUTE_LABELS: Record<AdministrationRoute, string> = {
    ORAL: 'Via Oral',
    IV: 'Intravenoso',
    IM: 'Intramuscular',
    SC: 'Subcutâneo',
    TOPICAL: 'Tópico',
    RECTAL: 'Retal',
    INHALATION: 'Inalatório',
    SUBLINGUAL: 'Sublingual',
    NASAL: 'Nasal',
    OPHTHALMIC: 'Oftálmico',
    OTIC: 'Auricular',
    ENTERAL: 'Enteral',
    EPIDURAL: 'Epidural',
    INTRATHECAL: 'Intratecal',
}
